import { ReactNode } from 'react';

import {
  Icons,
  LabelChip,
  LabelChipProps,
  mergeSx,
  SxProps,
  tokens,
} from '@cast/design-system';
import { Tendency } from '@cast/shared-types';

type Props = Omit<LabelChipProps, 'onDestroy' | 'iconChip'> & {
  tendency: Tendency;
  value: string;
  iconPlacement?: 'start' | 'end';
  upIsPositive?: boolean;
};

/**
 * @deprecated Use `TendencyLabelChip` from `@cast/design-system`
 * (components-v2) instead. Migration notes:
 * - `value` → `label`.
 * - Sizes: `2xs` → `xxs`, `sm` → `s`, `md` → `m`, `lg` → `l` (`xs` unchanged).
 * - `upIsPositive` and `iconPlacement` keep the same semantics.
 */
export const TendencyChip = ({
  tendency,
  value,
  iconPlacement = 'start',
  upIsPositive,
  ...props
}: Props) => {
  let icon: ReactNode = null;
  let sx: SxProps | undefined;

  switch (tendency) {
    case 'stable':
      icon = <Icons.Minus fill="bold" />;
      sx = {
        color: tokens.theme.fg.status.onNeutralSubtle,
        backgroundColor: tokens.theme.bg.status.neutralSubtle,
      };
      break;
    case 'down':
      icon = <Icons.ArrowDownRight />;
      sx = upIsPositive
        ? {
            color: tokens.theme.fg.status.onErrorStrong,
            backgroundColor: tokens.theme.bg.status.errorStrong,
          }
        : {
            color: tokens.theme.fg.status.onSuccessStrong,
            backgroundColor: tokens.theme.bg.status.successStrong,
          };
      break;
    case 'up':
      icon = <Icons.ArrowUpRight />;
      sx = upIsPositive
        ? {
            color: tokens.theme.fg.status.onSuccessStrong,
            backgroundColor: tokens.theme.bg.status.successStrong,
          }
        : {
            color: tokens.theme.fg.status.onErrorStrong,
            backgroundColor: tokens.theme.bg.status.errorStrong,
          };
      break;
  }

  return (
    <LabelChip
      {...props}
      sx={mergeSx(sx, props.sx)}
      {...(iconPlacement === 'start' ? { startIcon: icon } : { endIcon: icon })}
    >
      {value}
    </LabelChip>
  );
};
